import { SOLVE_V2_BASE_URL } from '../constants';
import { dashboardApi } from '../dashboard-api';
import {
  AiInstruction,
  AiInstructionBase,
  AiInstructionMetrics,
} from 'src/types/aiInstructionTypes';
import { SolveWidgetProduct } from 'src/types/types';

const aiInstructionApi = dashboardApi.injectEndpoints({
  endpoints: builder => ({
    createInstruction: builder.mutation<AiInstruction, AiInstructionBase>({
      invalidatesTags: ['AiInstructions'],
      query: body => ({
        body,
        method: 'POST',
        url: `${SOLVE_V2_BASE_URL}/ai-instruction`,
      }),
    }),
    createInstructionForAction: builder.mutation<
      AiInstruction,
      {
        body: AiInstructionBase;
        canvasActionId: string;
        intentWorkflowId: string;
        version: number;
      }
    >({
      invalidatesTags: ['AiInstructions'],
      query: ({ body, canvasActionId, intentWorkflowId, version }) => ({
        body,
        method: 'POST',
        url: `${SOLVE_V2_BASE_URL}/ai-instruction/${intentWorkflowId}/${version}/${canvasActionId}`,
      }),
    }),
    createInstructionForEmailWorkflowResponseStep: builder.mutation<
      AiInstruction,
      {
        body: AiInstructionBase;
        componentId: string;
        emailConfigurationId: string;
        intentDefinitionId: string;
        intentWorkflowId: string;
      }
    >({
      invalidatesTags: ['AiInstructions'],
      query: ({
        body,
        componentId,
        emailConfigurationId,
        intentDefinitionId,
        intentWorkflowId,
      }) => ({
        body,
        method: 'POST',
        url: `${SOLVE_V2_BASE_URL}/ai-instruction/email-workflow/${intentWorkflowId}/${intentDefinitionId}/${componentId}?email_configuration_id=${emailConfigurationId}`,
      }),
    }),
    deleteInstruction: builder.mutation<void, { aiInstructionId: string }>({
      invalidatesTags: ['AiInstructions'],
      query: ({ aiInstructionId }) => ({
        method: 'DELETE',
        url: `${SOLVE_V2_BASE_URL}/ai-instruction/${aiInstructionId}`,
      }),
    }),
    discardInstructionDraft: builder.mutation<
      void,
      { aiInstructionId: string }
    >({
      invalidatesTags: ['AiInstruction'],
      query: ({ aiInstructionId }) => ({
        method: 'POST',
        url: `${SOLVE_V2_BASE_URL}/ai-instruction/${aiInstructionId}/discard-draft`,
      }),
    }),
    getAiInstructionMetrics: builder.query<
      AiInstructionMetrics,
      { end?: number; start?: number } | void
    >({
      query: params => ({
        method: 'GET',
        params: params || {},
        url: `${SOLVE_V2_BASE_URL}/ai-instructions/metrics/aggregate`,
      }),
    }),
    getAiInstructionMetricsPerInstruction: builder.query<
      AiInstructionMetrics,
      { aiInstructionId: string; end?: number; start?: number }
    >({
      query: ({ aiInstructionId, end, start }) => ({
        method: 'GET',
        params: { end, start },
        url: `${SOLVE_V2_BASE_URL}/ai-instruction/metrics/${aiInstructionId}`,
      }),
    }),
    getInstruction: builder.query<AiInstruction, { aiInstructionId: string }>({
      providesTags: ['AiInstruction'],
      query: ({ aiInstructionId }) =>
        `${SOLVE_V2_BASE_URL}/ai-instruction/${aiInstructionId}`,
    }),
    getInstructions: builder.query<
      AiInstruction[],
      { draftMode?: 'published' | 'draft' | 'any' } | void
    >({
      providesTags: ['AiInstructions'],
      query: ({ draftMode = 'published' } = {}) => ({
        params: { draft_mode: draftMode },
        url: `${SOLVE_V2_BASE_URL}/ai-instructions`,
      }),
    }),
    getInstructionsForAction: builder.query<
      AiInstruction[],
      { canvasActionId: string; intentWorkflowId: string; version: number }
    >({
      providesTags: ['AiInstructions'],
      query: ({ canvasActionId, intentWorkflowId, version }) =>
        `${SOLVE_V2_BASE_URL}/ai-instructions/${intentWorkflowId}/${version}/${canvasActionId}`,
    }),
    getInstructionsForEmailWorkflowResponseStep: builder.query<
      AiInstruction[],
      { componentId: string; intentDefinitionId: string }
    >({
      providesTags: ['AiInstructions'],
      query: ({ componentId, intentDefinitionId }) =>
        `${SOLVE_V2_BASE_URL}/ai-instructions/email-workflow/${intentDefinitionId}/${componentId}`,
    }),
    getInstructionsForWorkflow: builder.query<
      AiInstruction[],
      { intentWorkflowId: string; product?: SolveWidgetProduct }
    >({
      providesTags: ['AiInstructions'],
      query: ({ intentWorkflowId, product }) => {
        const params = new URLSearchParams();
        if (product) {
          params.append('product', product);
        }
        const queryString = params.toString();
        return `${SOLVE_V2_BASE_URL}/ai-instructions/workflow/${intentWorkflowId}${
          queryString ? `?${queryString}` : ''
        }`;
      },
    }),
    publishInstruction: builder.mutation<
      AiInstruction,
      { aiInstructionId: string }
    >({
      invalidatesTags: ['AiInstructions', 'AiInstruction'],
      query: ({ aiInstructionId }) => ({
        method: 'POST',
        url: `${SOLVE_V2_BASE_URL}/ai-instruction/${aiInstructionId}/publish`,
      }),
    }),
    updateInstruction: builder.mutation<
      AiInstruction,
      { aiInstructionId: string; body: AiInstructionBase }
    >({
      query: ({ aiInstructionId, body }) => ({
        body,
        method: 'POST',
        url: `${SOLVE_V2_BASE_URL}/ai-instruction/${aiInstructionId}`,
      }),
    }),
  }),
});

export const {
  useCreateInstructionForActionMutation,
  useCreateInstructionForEmailWorkflowResponseStepMutation,
  useCreateInstructionMutation,
  useDeleteInstructionMutation,
  useDiscardInstructionDraftMutation,
  useGetAiInstructionMetricsPerInstructionQuery,
  useGetAiInstructionMetricsQuery,
  useGetInstructionQuery,
  useGetInstructionsForActionQuery,
  useGetInstructionsForEmailWorkflowResponseStepQuery,
  useGetInstructionsForWorkflowQuery,
  useGetInstructionsQuery,
  usePublishInstructionMutation,
  useUpdateInstructionMutation,
} = aiInstructionApi;
